export interface ChecklistItem {
  document: string;
  notes: string;
}

export interface VisaChecklist {
  title: string;
  items: ChecklistItem[];
}

export const UNIVERSAL_CHECKLIST: VisaChecklist = {
  title: 'Universal Documents (All Cases)',
  items: [
    {
      document: 'Passport (biographic page)',
      notes: 'Must be valid; used for identity and visa forms.',
    },
    {
      document: 'Updated CV / Resume',
      notes: 'Needed for drafting petition and letters.',
    },
    {
      document: 'Current U.S. Visa + I-94',
      notes: 'For applicants already in the U.S.',
    },
    {
      document: 'Previous U.S. immigration documents',
      notes: 'Includes visas, I-20s, DS-2019s, prior I-797s, prior petitions.',
    },
    {
      document: 'Diplomas & Academic Transcripts',
      notes: 'For education background.',
    },
    {
      document: 'Employment History (list or letters)',
      notes: 'Needed for forms and narrative.',
    },
    {
      document: 'Portfolio / Samples of work',
      notes: 'Especially important for creatives, engineers, researchers.',
    },
    {
      document: 'Address history (last 5 years)',
      notes: 'For I-129, DS-160, I-485, etc.',
    },
    {
      document: 'Travel history (5 years)',
      notes: 'If filing I-485, helps with security questions.',
    },
    {
      document: 'Proof of Name Change (if applicable)',
      notes: 'Marriage certificate, legal change documents, etc.',
    },
    {
      document: '2x2 Passport Photos',
      notes: 'For AOS or EAD cases.',
    },
  ],
};

export const VISA_SPECIFIC_CHECKLISTS: Record<string, VisaChecklist> = {
  'O-1A': {
    title: 'O-1A (Extraordinary Ability – STEM / Business / Sports / Science)',
    items: [
      {
        document: 'Expert Recommendation Letter List',
        notes: 'Names + emails. Information we will use to draft them.',
      },
      {
        document: 'Awards / Prizes',
        notes: 'Certificates, announcements. Must show national or international recognition.',
      },
      {
        document: 'Media Coverage / Press Articles',
        notes: 'PDF or link; includes interviews, features, mentions.',
      },
      {
        document: 'Judging Evidence',
        notes: 'Invitations, screenshots, certificates, emails.',
      },
      {
        document: 'High Salary Proof',
        notes: 'Paystubs, contracts, offer letters, payroll reports.',
      },
      {
        document: 'Patents / Inventions',
        notes: 'Patent certificates, filings, citations.',
      },
      {
        document: 'Published Research',
        notes: 'PDFs, DOIs, conference papers.',
      },
      {
        document: 'Invited Talks / Panels',
        notes: 'Flyers, programs, emails.',
      },
      {
        document: 'Evidence of Critical Role',
        notes: 'Employment verification, KPIs, organizational charts.',
      },
      {
        document: 'Employment Agreement / Proposed Role',
        notes: 'For the I-129 and itinerary.',
      },
    ],
  },
  'O-1B': {
    title: 'O-1B (Extraordinary Ability – Arts, TV/Film, Creative Fields)',
    items: [
      {
        document: 'Press / Media Coverage',
        notes: 'Reviews, interviews, articles, listings.',
      },
      {
        document: 'Awards / Nominations',
        notes: 'Festival awards, industry prizes.',
      },
      {
        document: 'Contracts or Deal Memos',
        notes: 'Needed for itinerary + agent petitions.',
      },
      {
        document: 'Portfolio',
        notes: 'Videos, reels, designs, showreels, performances.',
      },
      {
        document: 'Letters from Directors / Producers / Collaborators',
        notes: 'Information for us to draft the letters from expert testimonial.',
      },
      {
        document: 'Evidence of Commercial Success',
        notes: 'Box office, revenues, streaming numbers, social media stats.',
      },
      {
        document: 'Union Memberships',
        notes: 'SAG-AFTRA, AEA, etc.',
      },
      {
        document: 'Posters / Programs',
        notes: 'For productions or exhibitions.',
      },
      {
        document: 'Festival Acceptances',
        notes: 'Emails, certificates.',
      },
    ],
  },
  'EB-1A': {
    title: 'EB-1A (Permanent Residency – Extraordinary Ability)',
    items: [
      {
        document: 'Complete Evidence of All Claimed Criteria',
        notes: 'Publications, media, judging, patents, high salary, etc.',
      },
      {
        document: 'Citation Report',
        notes: 'Google Scholar, Web of Science. For researchers.',
      },
      {
        document: 'Awards Documentation',
        notes: 'Full description of prestige + selection criteria.',
      },
      {
        document: 'Employment Letters (critical role)',
        notes: 'Must describe duties, accomplishments, impact.',
      },
      {
        document: 'Proof of Original Contributions',
        notes: 'Internal documents, impact letters, product screenshots.',
      },
      {
        document: 'Press and Media Features',
        notes: 'High-quality sources preferred.',
      },
      {
        document: 'Recommendation Letters Contacts',
        notes: '6–10 names with relationships noted and information that we need to draft the letters.',
      },
      {
        document: 'Professional Memberships',
        notes: 'Must involve selective criteria.',
      },
    ],
  },
  'EB-2 NIW': {
    title: 'EB-2 NIW (National Interest Waiver)',
    items: [
      {
        document: 'Detailed CV with Publications and Projects',
        notes: 'Needed for drafting NIW plan.',
      },
      {
        document: 'Degrees & Diplomas',
        notes: 'Especially for advanced degree requirement.',
      },
      {
        document: 'Evidence of National Importance',
        notes: 'Government reports, market data, citations.',
      },
      {
        document: 'Letters from Employers / Partners',
        notes: 'Information that we will need to draft the letters.',
      },
      {
        document: 'Evidence of Work Impact',
        notes: 'Metrics, KPIs, research outcomes, product launches.',
      },
      {
        document: 'Media Coverage',
        notes: 'Relevant to showing influence.',
      },
      {
        document: 'Conference Presentations',
        notes: 'Programs, agendas, invitations.',
      },
      {
        document: 'Published Papers / Studies',
        notes: 'PDFs or links.',
      },
      {
        document: 'Proof of Future Plans in the U.S.',
        notes: 'Business plan, research plan, project roadmap.',
      },
    ],
  },
};
